/**
 * useAskArlo.js - Transcript state for the "What should I watch?" chat panel
 *
 * Wraps arloService.chat() streaming callbacks into React state. History is
 * held client-side ({role, content} pairs) and passed on every turn. Picks
 * resolve against the already-loaded categories first (_id, then slug) —
 * anything off the homepage payload hydrates through the channel API (same
 * rule as the rails; Arlo picks across the whole catalog). Picks that still
 * don't resolve drop. The SDK never throws into the page; every failure
 * lands in `error`. A turn-id guard makes cancel() win any race with the
 * async signal-gathering that precedes each chat() call and with pick
 * hydration. A 30s stall watchdog covers the SDK's silent xhr failure mode
 * (no onerror, no status check) by synthesizing the standard error.
 */
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppLoad } from '../contexts/AppLoadContext';
import arloService from '../services/ArloService';
import profileService from '../services/ProfileService';
import { buildChannelLookups } from '../utils/channelLookups';
import { hydrateMissingChannels } from '../utils/arloHydration';
import { resolveCustomerId } from '../utils/arloIdentity';
import { readStoredReactions, fetchWatchlistFavorites } from '../utils/arloSignals';

export const MAX_USER_TURNS = 6;

// Sports.TV-custom cold-start mood chips (classic movies + TV voice). Shown
// before the first message so a cold-start user gets a pick in one tap; a tap
// sends the phrase as an ordinary chat turn. Custom set rather than the SDK's
// DEFAULT_MOOD_CHIPS — same mechanism, Sports.TV wording.
export const MOOD_CHIPS = Object.freeze([
  'Surprise me',
  "A classic I've never seen",
  'Something cozy',
  'Make me laugh',
  'A feel-good oldie',
  'Good with the family',
]);

const CHAT_ERROR_COPY = "Couldn't get picks right now — try again.";

// The SDK's default xhr transport surfaces no onerror and never checks HTTP
// status — a dropped connection or rate-limit/auth error body produces NO
// callback at all. Without a host-side watchdog the turn would hang in
// streaming state forever ("Thinking…", send disabled, no retry).
const STALL_TIMEOUT_MS = 30000;

export const useAskArlo = () => {
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [attribution, setAttribution] = useState(null);
  // Per-turn quick-reply chips (Arlo's onChips). Live affordances, not
  // transcript: only the latest turn's set is actionable, cleared on the
  // next user action.
  const [chips, setChips] = useState([]);
  const { isAuthenticated } = useAuth0();
  const { categories } = useAppLoad();
  const handleRef = useRef(null);
  const turnIdRef = useRef(0);
  const stallTimerRef = useRef(null);

  const lookups = useMemo(() => buildChannelLookups(categories), [categories]);

  const turnsUsed = messages.filter(message => message.role === 'user').length;
  const turnCapReached = turnsUsed >= MAX_USER_TURNS;

  const cancel = useCallback(() => {
    turnIdRef.current += 1;
    clearTimeout(stallTimerRef.current);
    handleRef.current?.cancel();
    handleRef.current = null;
    setStreaming(false);
    // A cancelled turn's pick hydration resolves into the void (turn-id
    // guard), so clear any skeletons it left behind.
    setMessages(prev => {
      if (prev.length === 0 || !prev[prev.length - 1].pendingPicks) {return prev;}
      const next = prev.slice();
      next[next.length - 1] = { ...next[next.length - 1], pendingPicks: 0 };
      return next;
    });
  }, []);

  // Unmount cancels any in-flight turn (spec) — bump the turn id too so a
  // pending gatherSignals can't start a stray request, and kill the watchdog.
  useEffect(
    () => () => {
      turnIdRef.current += 1;
      clearTimeout(stallTimerRef.current);
      handleRef.current?.cancel();
    },
    [],
  );

  const gatherSignals = useCallback(async () => {
    if (!isAuthenticated) {return {};}
    try {
      const currentProfile = profileService.getCurrentProfile();
      const { likes, dislikes } = readStoredReactions(currentProfile?._id, lookups.byId);
      const favorites = await fetchWatchlistFavorites();
      return { likes, dislikes, favorites };
    } catch (e) {
      // Signals are an enhancement — a failed watchlist read never blocks the turn.
      return {};
    }
  }, [isAuthenticated, lookups]);

  const resolvePicks = useCallback(
    async cards => {
      const localChannel = id => lookups.byId.get(id) || lookups.bySlug.get(id);
      const missing = (cards || [])
        .map(card => card.content_id)
        .filter(id => id && !localChannel(id));
      const hydrated = missing.length > 0 ? await hydrateMissingChannels(missing) : new Map();

      return (cards || [])
        .map(card => {
          const channel = localChannel(card.content_id) || hydrated.get(card.content_id);
          return channel
            ? {
                channel,
                recId: card.rec_id,
                reason: typeof card.reason === 'string' ? card.reason : null,
              }
            : null;
        })
        .filter(Boolean);
    },
    [lookups],
  );

  // Appends into the trailing assistant stub — the last message is always
  // the stub for the turn in flight (send/retry create it synchronously).
  const patchLastMessage = useCallback(
    patch =>
      setMessages(prev => {
        if (prev.length === 0) {return prev;}
        const next = prev.slice();
        next[next.length - 1] = { ...next[next.length - 1], ...patch(next[next.length - 1]) };
        return next;
      }),
    [],
  );

  const startTurn = useCallback(
    async (text, history) => {
      const turnId = ++turnIdRef.current;
      setError(null);
      // A new turn is a user action — clear the previous turn's chips so stale
      // affordances don't linger (covers both send and retry).
      setChips([]);
      setStreaming(true);

      const signals = await gatherSignals();
      if (turnIdRef.current !== turnId) {return;} // cancelled/reset while gathering

      const armStallTimer = () => {
        clearTimeout(stallTimerRef.current);
        stallTimerRef.current = setTimeout(() => {
          if (turnIdRef.current !== turnId) {return;}
          handleRef.current?.cancel();
          handleRef.current = null;
          setError(CHAT_ERROR_COPY);
          setStreaming(false);
        }, STALL_TIMEOUT_MS);
      };
      armStallTimer();

      handleRef.current = arloService.chat(resolveCustomerId(), text, {
        history,
        signals,
        onToken: token => {
          armStallTimer();
          patchLastMessage(last => ({ content: last.content + token }));
        },
        onPicks: cards => {
          armStallTimer();
          // Hydration is async — surface the incoming card count immediately so
          // the panel can render skeletons, then swap in the resolved picks.
          // The turn-id check keeps a cancelled/reset turn's late-arriving
          // picks from patching the next turn's transcript.
          patchLastMessage(() => ({ pendingPicks: (cards || []).length }));
          resolvePicks(cards).then(picks => {
            if (turnIdRef.current !== turnId) {return;}
            patchLastMessage(() => ({ picks, pendingPicks: 0 }));
          });
        },
        onChips: chipList => {
          // Turn-id guard: a cancelled/reset turn's late chips must not steer
          // the next turn. The SDK also suppresses onChips after cancel(); this
          // is belt-and-suspenders. Never fires empty per the SDK contract, but
          // coerce defensively.
          if (turnIdRef.current !== turnId) {return;}
          armStallTimer();
          setChips(Array.isArray(chipList) ? chipList : []);
        },
        onAttribution: payload => {
          armStallTimer();
          setAttribution(payload);
        },
        onError: () => {
          clearTimeout(stallTimerRef.current);
          setError(CHAT_ERROR_COPY);
          setStreaming(false);
        },
        onDone: () => {
          clearTimeout(stallTimerRef.current);
          setStreaming(false);
        },
      });
    },
    [gatherSignals, resolvePicks, patchLastMessage],
  );

  const send = useCallback(
    text => {
      const trimmed = (text || '').trim();
      if (!trimmed || streaming || turnCapReached) {return;}

      const history = messages.map(({ role, content }) => ({ role, content }));
      setMessages(prev => [
        ...prev,
        { role: 'user', content: trimmed, picks: [] },
        { role: 'assistant', content: '', picks: [] },
      ]);
      startTurn(trimmed, history);
    },
    [messages, streaming, turnCapReached, startTurn],
  );

  const retry = useCallback(() => {
    if (streaming) {return;}
    let lastUserIndex = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        lastUserIndex = i;
        break;
      }
    }
    if (lastUserIndex === -1) {return;}

    const history = messages.slice(0, lastUserIndex).map(({ role, content }) => ({ role, content }));
    setMessages([
      ...messages.slice(0, lastUserIndex + 1),
      { role: 'assistant', content: '', picks: [] },
    ]);
    startTurn(messages[lastUserIndex].content, history);
  }, [messages, streaming, startTurn]);

  const reset = useCallback(() => {
    cancel();
    setMessages([]);
    setError(null);
    setChips([]);
  }, [cancel]);

  return {
    messages,
    streaming,
    error,
    attribution,
    chips,
    turnsUsed,
    turnCapReached,
    send,
    retry,
    reset,
    cancel,
  };
};

export default useAskArlo;
